import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Camera, Image as ImageIcon, Check } from 'lucide-react';
import axios from 'axios';

const ModelModal = ({ isOpen, onClose, mode, selectedModel, equipmentType, onSave }) => {
  const [currentMode, setCurrentMode] = useState(mode);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    manufacturer: '',
    price: '',
    description: '',
    time_to_manufacturer_inspection: '',
    validity_from_first_use: '',
    specification_file: null,
    instructions_for_use_file: null,
    declaration_of_conformity_file: null,
    inspection_record_file: null,
    image: null
  });
  const [isLifetime, setIsLifetime] = useState(false);

  const [imagePreview, setImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState({
    image: false,
    specification_file: false,
    instructions_for_use_file: false,
    declaration_of_conformity_file: false,
    inspection_record_file: false
  });
  const [existingFiles, setExistingFiles] = useState({
    specification_file: null,
    instructions_for_use_file: null,
    declaration_of_conformity_file: null,
    inspection_record_file: null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Global models functionality
  const [activeMode, setActiveMode] = useState('create'); // 'create' or 'global'
  const [globalModels, setGlobalModels] = useState([]);
  const [selectedGlobalModels, setSelectedGlobalModels] = useState(new Set());
  const [fetchingGlobal, setFetchingGlobal] = useState(false);
  const [hasAvailableGlobalModels, setHasAvailableGlobalModels] = useState(null); // null = not checked, true = has models, false = no models

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy'}/api`;

  // Update current mode when prop changes
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  // Reset form when modal opens/closes or when selectedModel changes
  useEffect(() => {
    if (isOpen) {
      if ((currentMode === 'edit' || currentMode === 'view') && selectedModel) {
        const inspectionValue = selectedModel.time_to_manufacturer_inspection;
        setFormData({
          name: selectedModel.name || '',
          brand: selectedModel.brand || '',
          manufacturer: selectedModel.manufacturer || '',
          price: selectedModel.price || '',
          description: selectedModel.description || '',
          time_to_manufacturer_inspection: inspectionValue === 100 ? '' : (inspectionValue || ''),
          validity_from_first_use: selectedModel.validity_from_first_use || '',
          specification_file: null, // File uploads need to be handled separately
          instructions_for_use_file: null,
          declaration_of_conformity_file: null,
          inspection_record_file: null,
          image: null
        });
        setIsLifetime(inspectionValue === 100);
        // Set image preview if model has existing image
        setImagePreview(selectedModel.image || null);
        // Set existing files if model has them
        setExistingFiles({
          specification_file: selectedModel.specification_file || null,
          instructions_for_use_file: selectedModel.instructions_for_use_file || null,
          declaration_of_conformity_file: selectedModel.declaration_of_conformity_file || null,
          inspection_record_file: selectedModel.inspection_record_file || null
        });
        setActiveMode('create'); // Always start with create mode for edit/view
      } else {
        setFormData({
          name: '',
          brand: '',
          manufacturer: '',
          price: '',
          description: '',
          time_to_manufacturer_inspection: '',
          validity_from_first_use: '',
          specification_file: null,
          instructions_for_use_file: null,
          declaration_of_conformity_file: null,
          inspection_record_file: null,
          image: null
        });
        setIsLifetime(false);
        setImagePreview(null);
        setExistingFiles({
          specification_file: null,
          instructions_for_use_file: null,
          declaration_of_conformity_file: null,
          inspection_record_file: null
        });
        setActiveMode('create');
      }
      setSelectedGlobalModels(new Set());
      setGlobalModels([]); // Clear global models when modal opens
      setHasAvailableGlobalModels(null); // Reset availability check
      setErrors({});
    }
  }, [isOpen, currentMode, selectedModel]);

  // Check if there are available global models when modal opens in create mode
  useEffect(() => {
    const checkAvailableGlobalModels = async () => {
      // Only check for create mode with global_type_id
      if (!isOpen || currentMode !== 'create' || !equipmentType?.global_type_id) {
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        };

        // Fetch global models and company models in parallel
        const [globalResponse, companyResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=global&type=${equipmentType.global_type_id}`, config),
          axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=company&type=${equipmentType.global_type_id}`, config)
        ]);

        // Create a Set of company models' global_model_id for quick lookup
        const copiedGlobalModelIds = new Set(
          companyResponse.data
            .filter(model => model.global_model_id)
            .map(model => model.global_model_id)
        );

        // Filter out global models that are already copied
        const availableGlobalModels = globalResponse.data.filter(globalModel => {
          return !copiedGlobalModelIds.has(globalModel.id);
        });

        // Set availability based on whether there are any available models
        setHasAvailableGlobalModels(availableGlobalModels.length > 0);
      } catch (err) {
        console.error('Error checking global models availability:', err);
        // On error, assume no models available
        setHasAvailableGlobalModels(false);
      }
    };

    checkAvailableGlobalModels();
  }, [isOpen, currentMode, equipmentType, API_BASE_URL]);

  const handleFileChange = (file, fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));

    // Handle image preview
    if (fieldName === 'image' && file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
        setErrors(prev => ({
          ...prev,
          image: 'Please select a valid image file'
        }));
      }
    } else if (fieldName === 'image' && !file) {
      setImagePreview(null);
    }

    // Handle file selection - hide existing file when new one is selected
    if (['specification_file', 'instructions_for_use_file', 'declaration_of_conformity_file', 'inspection_record_file'].includes(fieldName) && file) {
      setExistingFiles(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }

    // Clear error when file is selected
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0] || null;
      handleFileChange(file, name);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e, fieldName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleDragLeave = (e, fieldName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [fieldName]: false }));
  };

  const handleDrop = (e, fieldName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [fieldName]: false }));

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (fieldName === 'image') {
        if (file.type.startsWith('image/')) {
          handleFileChange(file, fieldName);
        } else {
          setErrors(prev => ({
            ...prev,
            image: 'Please select a valid image file'
          }));
        }
      } else if (['specification_file', 'instructions_for_use_file', 'declaration_of_conformity_file', 'inspection_record_file'].includes(fieldName)) {
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (allowedTypes.includes(file.type) || file.name.toLowerCase().endsWith('.pdf') || file.name.toLowerCase().endsWith('.doc') || file.name.toLowerCase().endsWith('.docx')) {
          handleFileChange(file, fieldName);
        } else {
          setErrors(prev => ({
            ...prev,
            [fieldName]: 'Please select a valid PDF or DOC file'
          }));
        }
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Model name is required';
    }

    if (!formData.brand.trim()) {
      newErrors.brand = 'Brand is required';
    }

    if (formData.price && isNaN(parseFloat(formData.price))) {
      newErrors.price = 'Price must be a valid number';
    }

    // Validate time_to_manufacturer_inspection based on isLifetime checkbox
    if (!isLifetime) {
      // If not lifetime, field is required
      if (!formData.time_to_manufacturer_inspection) {
        newErrors.time_to_manufacturer_inspection = 'Manufacturer\'s validity is required (or check "Is Lifetime")';
      } else if (isNaN(parseInt(formData.time_to_manufacturer_inspection)) || parseInt(formData.time_to_manufacturer_inspection) < 1) {
        newErrors.time_to_manufacturer_inspection = 'Manufacturer\'s validity must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch global models when switching to global mode
  const fetchGlobalModels = async () => {
    if (!equipmentType || !equipmentType.global_type_id) {
      setErrors(prev => ({ ...prev, global: 'This equipment type was not copied from global database. Cannot show global models.' }));
      return;
    }

    setFetchingGlobal(true);
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Fetch global models
      const globalResponse = await axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=global&type=${equipmentType.global_type_id}`, config);

      // Fetch company models to check which global models are already copied
      const companyResponse = await axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=company&type=${equipmentType.global_type_id}`, config);

      // Create a Set of company models' global_model_id for quick lookup
      const copiedGlobalModelIds = new Set(
        companyResponse.data
          .filter(model => model.global_model_id)  // Only models copied from global
          .map(model => model.global_model_id)
      );

      // Filter out global models that are already copied to company
      const availableGlobalModels = globalResponse.data.filter(globalModel => {
        return !copiedGlobalModelIds.has(globalModel.id);
      });

      setGlobalModels(availableGlobalModels);
    } catch (err) {
      console.error('Error fetching global models:', err);
      setErrors(prev => ({ ...prev, global: 'Failed to load global models. Please try again.' }));
    } finally {
      setFetchingGlobal(false);
    }
  };

  // Handle mode switch
  const handleModeSwitch = (newMode) => {
    setActiveMode(newMode);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.global;
      return newErrors;
    });

    if (newMode === 'global') {
      // Always fetch fresh data when switching to global mode
      fetchGlobalModels();
    }
  };

  // Handle global model selection
  const handleGlobalModelToggle = (modelId) => {
    setSelectedGlobalModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      if (activeMode === 'create') {
        // Create or edit mode - validate form and use existing onSave mechanism
        if (!validateForm()) {
          setLoading(false);
          return;
        }

        // Create FormData for file uploads
        const submitFormData = new FormData();

        // Add basic fields
        submitFormData.append('name', formData.name.trim());
        submitFormData.append('brand', formData.brand.trim());
        if (formData.manufacturer.trim()) {
          submitFormData.append('manufacturer', formData.manufacturer.trim());
        }
        if (formData.price) {
          submitFormData.append('price', formData.price);
        }
        if (formData.description.trim()) {
          submitFormData.append('description', formData.description.trim());
        }
        // Handle time_to_manufacturer_inspection: send 100 if lifetime, otherwise use field value
        if (isLifetime) {
          submitFormData.append('time_to_manufacturer_inspection', '100');
        } else if (formData.time_to_manufacturer_inspection) {
          submitFormData.append('time_to_manufacturer_inspection', formData.time_to_manufacturer_inspection);
        }
        if (formData.validity_from_first_use) {
          submitFormData.append('validity_from_first_use', formData.validity_from_first_use);
        }

        // Mark as company-specific model
        submitFormData.append('company_specific', 'true');

        // Add files if selected
        if (formData.image) {
          submitFormData.append('image', formData.image);
        }
        if (formData.specification_file) {
          submitFormData.append('specification_file', formData.specification_file);
        }
        if (formData.instructions_for_use_file) {
          submitFormData.append('instructions_for_use_file', formData.instructions_for_use_file);
        }
        if (formData.declaration_of_conformity_file) {
          submitFormData.append('declaration_of_conformity_file', formData.declaration_of_conformity_file);
        }
        if (formData.inspection_record_file) {
          submitFormData.append('inspection_record_file', formData.inspection_record_file);
        }

        // Handle file removals in edit mode
        const fileFields = ['specification_file', 'instructions_for_use_file', 'declaration_of_conformity_file', 'inspection_record_file'];
        fileFields.forEach(fieldName => {
          if (currentMode === 'edit' && !existingFiles[fieldName] && !formData[fieldName]) {
            submitFormData.append(`remove_${fieldName}`, 'true');
          }
        });

        console.log('DEBUG: ModelModal submitting FormData with files');
        console.log('DEBUG: ModelModal equipmentType:', equipmentType);

        await onSave(submitFormData);
      } else {
        // Global mode - copy selected models using new endpoint
        if (selectedGlobalModels.size === 0) {
          setErrors({ global: 'Please select at least one model to add' });
          setLoading(false);
          return;
        }

        const copyData = {
          model_ids: Array.from(selectedGlobalModels),
          equipment_type_id: equipmentType.id
        };

        await axios.post(`${API_BASE_URL}/safetyequipmentmodel/copy-global/`, copyData, config);
        // Signal success to parent component for data refresh, but don't pass form data
        if (onSave) {
          try {
            onSave({ __globalCopySuccess: true }); // Special flag to indicate success without form processing
          } catch (e) {
            console.log('Parent onSave failed, but models were copied successfully');
          }
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving model:', error);
      console.log('Error response data:', error.response?.data);

      let errorMessage = 'Failed to save model. Please try again.';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors[0];
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }

      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalTitle = currentMode === 'edit' ? 'Edit Equipment Model' : 
                     currentMode === 'view' ? 'View Equipment Model' : 
                     'Add New Equipment Model';

  // Function to switch from view to edit mode
  const switchToEditMode = () => {
    setCurrentMode('edit');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 h-[95vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
            {equipmentType && (
              <p className="text-sm text-gray-600 mt-1">
                For equipment type: <span className="font-medium">{equipmentType.name}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - Two Panel Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Main Form */}
          <div className={`${activeMode === 'create' ? 'w-2/3 border-r' : 'w-full'} flex flex-col`}>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* Error Messages */}
              {errors.submit && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {errors.submit}
                </div>
              )}
              {errors.global && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {errors.global}
                </div>
              )}

          {/* Mode Selection (only for create mode) */}
          {currentMode === 'create' && (
            <div className="mb-6">
              {equipmentType && equipmentType.global_type_id ? (
                // Check if there are available global models
                hasAvailableGlobalModels === true ? (
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('create')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeMode === 'create'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Create New Model
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('global')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeMode === 'global'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Add from Global Database
                    </button>
                  </div>
                ) : hasAvailableGlobalModels === false ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-blue-600">
                      <strong>Info:</strong> All global models for this equipment type have already been added to your company.
                    </p>
                  </div>
                ) : null
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-600">
                    <strong>Note:</strong> This equipment type was created manually. To add models from global database,
                    use equipment types that were copied from the global database.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Create New Model Form */}
          {activeMode === 'create' && (
            <>
              <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="e.g., Pro Series 2000"
                  disabled={loading}
                  readOnly={currentMode === 'view'}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand *
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.brand ? 'border-red-500' : 'border-gray-300'
                  } ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="e.g., 3M, MSA, Honeywell"
                  disabled={loading}
                  readOnly={currentMode === 'view'}
                />
                {errors.brand && (
                  <p className="mt-1 text-sm text-red-600">{errors.brand}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manufacturer
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="Full manufacturer name"
                  disabled={loading}
                  readOnly={currentMode === 'view'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (USD)
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.price ? 'border-red-500' : 'border-gray-300'
                  } ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="0.00"
                  disabled={loading}
                  readOnly={currentMode === 'view'}
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Left half: divided into 2 equal parts */}
              <div className="grid grid-cols-2 gap-3 items-end">
                {/* First part: Validity + years */}
                <div className="flex gap-2 items-end">
                  <div className="flex-shrink-0" style={{ width: '80px' }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validity *
                    </label>
                    <input
                      type="number"
                      name="time_to_manufacturer_inspection"
                      value={formData.time_to_manufacturer_inspection}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.time_to_manufacturer_inspection ? 'border-red-500' : 'border-gray-300'
                      } ${currentMode === 'view' ? 'bg-gray-50' : ''} ${isLifetime ? 'bg-gray-100' : ''}`}
                      disabled={loading || isLifetime || currentMode === 'view'}
                      readOnly={currentMode === 'view'}
                    />
                    {errors.time_to_manufacturer_inspection && (
                      <p className="mt-1 text-sm text-red-600">{errors.time_to_manufacturer_inspection}</p>
                    )}
                  </div>
                  <div className="pb-2">
                    <span className="text-sm text-gray-600">years</span>
                  </div>
                </div>

                {/* Second part: Is Lifetime checkbox */}
                <div className="pb-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isLifetime}
                      onChange={(e) => {
                        setIsLifetime(e.target.checked);
                        if (e.target.checked) {
                          // Clear any errors when checking lifetime
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.time_to_manufacturer_inspection;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={loading || currentMode === 'view'}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Is Lifetime</span>
                  </label>
                </div>
              </div>

              {/* Right half: Validity from First Use */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validity from First Use (years)
                </label>
                <input
                  type="number"
                  name="validity_from_first_use"
                  value={formData.validity_from_first_use}
                  onChange={handleInputChange}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.validity_from_first_use ? 'border-red-500' : 'border-gray-300'
                  } ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="e.g., 10"
                  disabled={loading}
                  readOnly={currentMode === 'view'}
                />
                {errors.validity_from_first_use && (
                  <p className="mt-1 text-sm text-red-600">{errors.validity_from_first_use}</p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${currentMode === 'view' ? 'bg-gray-50' : ''}`}
              placeholder="Model description, features, specifications..."
              disabled={loading}
              readOnly={currentMode === 'view'}
            />
          </div>

          {/* Model Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model Image
            </label>
            {currentMode !== 'view' ? (
              <div
                className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                  dragOver.image ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                }`}
                onDragOver={(e) => handleDragOver(e, 'image')}
                onDragLeave={(e) => handleDragLeave(e, 'image')}
                onDrop={(e) => handleDrop(e, 'image')}
              >
                <div className="space-y-1 text-center">
                  {imagePreview ? (
                    <div className="space-y-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto h-24 w-24 object-cover rounded-md"
                      />
                      <div className="flex text-sm text-gray-600 justify-center">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          <span>Change</span>
                          <input
                            type="file"
                            name="image"
                            onChange={handleInputChange}
                            className="sr-only"
                            accept="image/*"
                            disabled={loading}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <ImageIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <div className="text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          <span>Upload</span>
                          <input
                            type="file"
                            name="image"
                            onChange={handleInputChange}
                            className="sr-only"
                            accept="image/*"
                            disabled={loading}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  {errors.image && (
                    <p className="text-sm text-red-600 mt-2">{errors.image}</p>
                  )}
                </div>
              </div>
            ) : imagePreview ? (
              <div className="border border-gray-300 rounded-md p-4">
                <img
                  src={imagePreview}
                  alt="Model"
                  className="mx-auto h-24 w-24 object-cover rounded-md"
                />
              </div>
            ) : (
              <div className="text-sm text-gray-500">No image uploaded</div>
            )}
          </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}
            </>
          )}

          {/* Global Models Selection */}
          {activeMode === 'global' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Global Equipment Models</h3>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedGlobalModels.size} of {globalModels.length} selected
                  </span>
                  {globalModels.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedGlobalModels.size === globalModels.length) {
                          // Deselect all
                          setSelectedGlobalModels(new Set());
                        } else {
                          // Select all
                          setSelectedGlobalModels(new Set(globalModels.map(m => m.id)));
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {selectedGlobalModels.size === globalModels.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
              </div>

              {fetchingGlobal ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading global models...</p>
                </div>
              ) : globalModels.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No global equipment models available for this type</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-md">
                  {globalModels.map((model) => (
                    <div
                      key={model.id}
                      className={`flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer ${
                        selectedGlobalModels.has(model.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleGlobalModelToggle(model.id)}
                    >
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                        selectedGlobalModels.has(model.id)
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300'
                      }`}>
                        {selectedGlobalModels.has(model.id) && <Check className="w-3 h-3" />}
                      </div>

                      {/* Image */}
                      <div className="w-16 h-16 mr-3 flex-shrink-0 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                        {model.image && model.image !== '' ? (
                          <img
                            src={model.image}
                            alt={model.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400 text-center px-1">No image</span>';
                            }}
                          />
                        ) : (
                          <span className="text-xs text-gray-400 text-center px-1">No image</span>
                        )}
                      </div>

                      {/* Model info */}
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate cursor-help" title={model.name}>
                          {model.name}
                        </h4>
                        {model.brand && (
                          <p className="text-xs text-gray-500 truncate cursor-help" title={`Brand: ${model.brand}`}>
                            Brand: {model.brand}
                          </p>
                        )}
                        {model.description && (
                          <p className="text-xs text-gray-500 truncate cursor-help mt-1" title={model.description}>
                            {model.description}
                          </p>
                        )}
                        {model.price && (
                          <p className="text-xs text-gray-500 mt-1">
                            Price: ${model.price}
                          </p>
                        )}
                      </div>

                      {/* Right section - Files grid */}
                      <div className="flex ml-4 flex-shrink-0">
                        {/* Specification column */}
                        <div className="flex flex-col items-center justify-center text-center" style={{ width: '90px' }}>
                          <div className="text-xs text-gray-600 mb-1">Specification</div>
                          <div>
                            {model.specification_file && model.specification_file !== '' ? (
                              <span className="text-green-600 font-medium">✓</span>
                            ) : (
                              <span className="text-red-500 font-medium">✗</span>
                            )}
                          </div>
                        </div>

                        {/* Instructions column */}
                        <div className="flex flex-col items-center justify-center text-center" style={{ width: '90px' }}>
                          <div className="text-xs text-gray-600 mb-1">Instructions</div>
                          <div>
                            {model.instructions_for_use_file && model.instructions_for_use_file !== '' ? (
                              <span className="text-green-600 font-medium">✓</span>
                            ) : (
                              <span className="text-red-500 font-medium">✗</span>
                            )}
                          </div>
                        </div>

                        {/* Declaration column */}
                        <div className="flex flex-col items-center justify-center text-center" style={{ width: '90px' }}>
                          <div className="text-xs text-gray-600 mb-1">Declaration</div>
                          <div>
                            {model.declaration_of_conformity_file && model.declaration_of_conformity_file !== '' ? (
                              <span className="text-green-600 font-medium">✓</span>
                            ) : (
                              <span className="text-red-500 font-medium">✗</span>
                            )}
                          </div>
                        </div>

                        {/* Inspection column */}
                        <div className="flex flex-col items-center justify-center text-center" style={{ width: '90px' }}>
                          <div className="text-xs text-gray-600 mb-1">Inspection</div>
                          <div>
                            {model.inspection_record_file && model.inspection_record_file !== '' ? (
                              <span className="text-green-600 font-medium">✓</span>
                            ) : (
                              <span className="text-red-500 font-medium">✗</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
            </div>
          </div>

          {/* Right Panel - Documents (only visible in create mode) */}
          {activeMode === 'create' && (
            <div className="w-1/3 flex flex-col">
              <div className="p-6 bg-gray-50 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Documents & Files</h3>
                <p className="text-sm text-gray-600 mt-1">Upload model-related documents</p>
              </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* File Upload Sections */}
              {[
                { key: 'specification_file', label: 'Specification File', icon: FileText },
                { key: 'instructions_for_use_file', label: 'Instructions for Use', icon: FileText },
                { key: 'declaration_of_conformity_file', label: 'Declaration of Conformity', icon: FileText },
                { key: 'inspection_record_file', label: 'Inspection Record Template', icon: FileText }
              ].map(({ key, label, icon: Icon }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                  </label>
                  {currentMode !== 'view' ? (
                    <div
                      className={`flex justify-center px-4 pt-4 pb-4 border-2 border-dashed rounded-md transition-colors ${
                        dragOver[key] ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                      }`}
                      onDragOver={(e) => handleDragOver(e, key)}
                      onDragLeave={(e) => handleDragLeave(e, key)}
                      onDrop={(e) => handleDrop(e, key)}
                    >
                      <div className="space-y-1 text-center">
                        <Icon className="mx-auto h-6 w-6 text-gray-400" />
                        <div className="text-sm text-gray-600">
                          <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                            <span>Upload</span>
                            <input
                              type="file"
                              name={key}
                              onChange={handleInputChange}
                              className="sr-only"
                              accept=".pdf,.doc,.docx"
                              disabled={loading}
                            />
                          </label>
                        </div>
                        {formData[key] && (
                          <p className="text-xs text-green-600 mt-1">
                            {formData[key].name}
                          </p>
                        )}
                        {!formData[key] && existingFiles[key] && (
                          <div className="mt-1">
                            <a
                              href={existingFiles[key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center justify-center"
                            >
                              <Icon className="h-3 w-3 mr-1" />
                              Current file
                            </a>
                            <button
                              type="button"
                              onClick={() => setExistingFiles(prev => ({ ...prev, [key]: null }))}
                              className="text-xs text-red-600 hover:text-red-800 underline mt-1"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {errors[key] && (
                          <p className="text-xs text-red-600 mt-1">{errors[key]}</p>
                        )}
                      </div>
                    </div>
                  ) : existingFiles[key] ? (
                    <div className="border border-gray-300 rounded-md p-3">
                      <a
                        href={existingFiles[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        View file
                      </a>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No file uploaded</div>
                  )}
                </div>
              ))}
            </div>
            </div>
          )}
        </div>

        {/* Actions - Fixed Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {currentMode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {currentMode === 'view' && (
              <button
                type="button"
                onClick={switchToEditMode}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Edit
              </button>
            )}
            {currentMode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                )}
                <span>
                  {loading ? 'Saving...' :
                   activeMode === 'create' ? (currentMode === 'edit' ? 'Update Model' : 'Create Model') :
                   `Add ${selectedGlobalModels.size} Model${selectedGlobalModels.size !== 1 ? 's' : ''}`}
                </span>
              </button>
            )}
          </div>
      </form>
    </div>
  );
};

export default ModelModal;